import {
  ActionFactory,
  EditActionDefinition,
  HostEditAction,
  ActionNames,
  EditActionBase as SystemAction,
  ContextBase as SystemActionContext,
  SystemActionNames,
} from '@wix/editor-platform-host-integration-apis';
import { Actions } from '@wix/component-protocol/schema';

import { createCustomisationAPI } from '../customActions/customisationAPI';
import {
  ActionFactoryWithPlatformContext,
  ContributedEditActionsDict,
  ContributedPlatformActionsDict,
  PlatformContext,
  PlatformEditAction,
} from '../types';

const isActionFactory = <TContext, Type extends EditActionDefinition>(
  action: EditActionDefinition | ActionFactory<TContext, Type>,
): action is ActionFactory<TContext, Type> => typeof action === 'function';

const actionsResolver = <TAction extends EditActionDefinition, TContext>(
  action: TAction | ActionFactory<TContext, TAction>,
  context: TContext,
): TAction => (isActionFactory(action) ? action(context) : action);

export const resolveSystemActionsWithFactories = <
  TActionDefinition extends EditActionDefinition = EditActionDefinition,
>(
  actions: SystemAction<SystemActionNames>[],
  context: PlatformContext,
): TActionDefinition[] => {
  return actions.map((action) =>
    actionsResolver<SystemAction, SystemActionContext>(action, {
      editorPointer: context.editorPointer,
    }),
  ) as TActionDefinition[];
};

export const resolveCustomActionsWithFactories = <
  TActionDefinition extends EditActionDefinition = EditActionDefinition,
>(
  actions: Record<string, ActionFactoryWithPlatformContext>,
  manifestActions: Actions,
  context: PlatformContext,
): TActionDefinition[] => {
  return Object.entries(manifestActions).map(([actionName, action]) => {
    const customisationAPI = createCustomisationAPI(
      action,
      actionName,
      context,
    );
    return actionsResolver(actions[actionName] as PlatformEditAction, {
      ...context,
      customisationAPI,
    });
  }) as TActionDefinition[];
};

export const resolvePlatformActionsWithFactories = <
  TActionDefinition extends EditActionDefinition = EditActionDefinition,
>(
  actions: ContributedPlatformActionsDict,
  manifestActions: Actions,
  context: PlatformContext,
): TActionDefinition[] => {
  return Object.entries(manifestActions)
    .map(([actionName, action]) => {
      const customisationAPI = createCustomisationAPI(
        action,
        actionName,
        context,
      );
      return actionsResolver(
        actions[actionName as ActionNames] as PlatformEditAction,
        {
          ...context,
          customisationAPI,
        },
      );
    })
    .filter((action) => !!action) as TActionDefinition[];
};

export const resolveHostActionsWithFactories = <
  TActionDefinition extends EditActionDefinition = EditActionDefinition,
>(
  actions: ContributedEditActionsDict,
  manifestActions: Actions,
  context: PlatformContext,
): TActionDefinition[] => {
  return Object.entries(manifestActions)
    .map(([actionName, action]) => {
      const customisationAPI = createCustomisationAPI(
        action,
        actionName,
        context,
      );
      return actionsResolver(
        actions[actionName as ActionNames] as HostEditAction,
        {
          editorPointer: context.editorPointer,
          customisationAPI,
        },
      );
    })
    .filter((action) => {
      if (!action) {
        return false;
      }

      const shouldKeep = !(
        'shouldBeExcluded' in action && !!action.shouldBeExcluded?.()
      );

      return shouldKeep;
    }) as TActionDefinition[];
};
